import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type {
  FindingActionKind,
  ValidationIssue,
} from "../../types";
import FindingContextCard from "./FindingContextCard";

// A finding's display group for the popover heading.
const TYPE_TITLES: Record<string, string> = {
  chronology: "Chronology",
  financial: "Financial",
  attribution: "Attribution",
  completeness: "Completeness",
  coverage: "Completeness",
  hallucination: "Hallucination",
  date_grounding: "Date",
  amount: "Amount",
  citation: "Citation",
  uncited_claim: "Uncited claim",
};

export function findingTitle(type: string): string {
  return TYPE_TITLES[type] ?? type;
}

// Inline ⚠ marker for a single finding. Amber = unaddressed, gray =
// accepted, (dismissed findings aren't rendered), red = flagged-for-edit.
// Clicking opens a popover with the finding detail + three actions.
export default function FindingMarker({
  finding,
  action,
  note,
  onAction,
  inline = true,
}: {
  finding: ValidationIssue;
  action: FindingActionKind | undefined;
  // The correction note already saved for this finding (when flagged).
  note?: string;
  onAction: (kind: FindingActionKind, note?: string) => void;
  // inline=true renders as a superscript marker in prose; false renders a
  // plain inline-flex marker (for summary-bar rows).
  inline?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // When set, the note input is shown below the actions; clicking "Flag"
  // commits onAction("flagged", note). Reset whenever the popover closes.
  const [flagging, setFlagging] = useState(false);
  const [noteText, setNoteText] = useState(note ?? "");
  const ref = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  // Placement flips to keep the popover on-screen: x "right" anchors the
  // popover's right edge to the marker (opens leftward); y "up" flips it
  // above the marker.
  const [place, setPlace] = useState<{ x: "left" | "right"; y: "down" | "up" }>(
    { x: "left", y: "down" },
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // After opening, measure the popover (rendered at its default down/left
  // placement) and flip horizontally/vertically if it overflows the
  // viewport. Runs pre-paint so there's no visible jump.
  useLayoutEffect(() => {
    if (!open) {
      setPlace({ x: "left", y: "down" });
      // Closing the popover discards an in-progress flag-note edit.
      setFlagging(false);
      setNoteText(note ?? "");
      return;
    }
    const el = popRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pad = 8;
    setPlace({
      x: r.right > window.innerWidth - pad ? "right" : "left",
      y: r.bottom > window.innerHeight - pad ? "up" : "down",
    });
  }, [open, note]);

  const cls =
    action === "accepted"
      ? "accepted"
      : action === "flagged"
        ? "flagged"
        : "open";
  const glyph = action === "accepted" ? "✓" : action === "flagged" ? "✗" : "⚠";

  const Wrapper = inline ? "sup" : "span";

  return (
    <span className="finding-marker-wrap" ref={ref}>
      <Wrapper>
        <button
          type="button"
          className={`finding-marker ${cls}`}
          title={`${findingTitle(finding.type)} — click to review`}
          aria-label={`${findingTitle(finding.type)} finding`}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          {glyph}
        </button>
      </Wrapper>
      {open && (
        <div
          ref={popRef}
          className={`finding-popover pop-x-${place.x} pop-y-${place.y}`}
          role="dialog"
        >
          <div className="finding-popover-type">{findingTitle(finding.type)}</div>
          <div className="finding-popover-desc">{finding.description}</div>
          <FindingContextCard finding={finding} />
          <div className="finding-popover-actions">
            <button
              type="button"
              className="finding-act dismiss"
              title="This finding is incorrect — remove it (false positive)"
              onClick={() => {
                onAction("dismissed");
                setOpen(false);
              }}
            >
              Dismiss
            </button>
            <button
              type="button"
              className={`finding-act accept${action === "accepted" ? " on" : ""}`}
              title="This finding is correct but I'm okay with it — acknowledge and proceed"
              onClick={() => {
                onAction("accepted");
                setOpen(false);
              }}
            >
              Accept
            </button>
            <button
              type="button"
              className={`finding-act flag${
                action === "flagged" || flagging ? " on" : ""
              }`}
              title="This needs to be fixed — mark for attention"
              onClick={() => {
                // Open the note input rather than flagging immediately, so
                // the user can attach an optional correction note.
                setNoteText(note ?? "");
                setFlagging(true);
              }}
            >
              Flag for edit
            </button>
          </div>
          {flagging && (
            <div className="finding-flag-note">
              <textarea
                className="finding-flag-note-input"
                placeholder="Add a correction note..."
                value={noteText}
                rows={2}
                autoFocus
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => {
                  // Cmd/Ctrl+Enter commits; Escape cancels the note edit.
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    onAction("flagged", noteText);
                    setOpen(false);
                  } else if (e.key === "Escape") {
                    e.stopPropagation();
                    setFlagging(false);
                  }
                }}
              />
              <button
                type="button"
                className="finding-act flag on"
                title="Flag this finding for correction on the next regenerate"
                onClick={() => {
                  onAction("flagged", noteText);
                  setOpen(false);
                }}
              >
                Flag
              </button>
            </div>
          )}
          {!flagging && action === "flagged" && note && (
            <div className="finding-flag-saved-note">
              <span className="finding-flag-saved-label">Your note:</span>{" "}
              {note}
            </div>
          )}
        </div>
      )}
    </span>
  );
}
